import { Injectable, signal } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class FullscreenService {
  private readonly _isFullscreen = signal(false);
  readonly isFullscreen = this._isFullscreen.asReadonly();

  /* istanbul ignore next */
  constructor() {
    // 监听全屏状态变化
    document.addEventListener('fullscreenchange', () => {
      this._isFullscreen.set(!!document.fullscreenElement);
    });

    document.addEventListener('webkitfullscreenchange', () => {
      this._isFullscreen.set(!!(document as any).webkitFullscreenElement);
    });

    document.addEventListener('mozfullscreenchange', () => {
      this._isFullscreen.set(!!(document as any).mozFullScreenElement);
    });

    document.addEventListener('MSFullscreenChange', () => {
      this._isFullscreen.set(!!(document as any).msFullscreenElement);
    });
  }

  public isMobileOrTablet(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Macintosh/i.test(navigator.userAgent);
  }

  /**
   * 请求进入全屏模式
   */
  /* istanbul ignore next */
  async requestFullscreen(): Promise<void> {
    try {
      const element = document.getElementsByTagName('body')[0] || document.documentElement;

      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        await (element as any).webkitRequestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        await (element as any).mozRequestFullScreen();
      } else if ((element as any).msRequestFullscreen) {
        await (element as any).msRequestFullscreen();
      } else {
        console.warn('Fullscreen API not supported');
      }
    } catch (error) {
      console.warn('Failed to enter fullscreen:', error);
    }
  }

  /**
   * 退出全屏模式
   */
  /* istanbul ignore next */
  async exitFullscreen(): Promise<void> {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
    } catch (error) {
      console.error('Failed to exit fullscreen:', error);
    }
  }

  /**
   * 切换全屏状态
   */
  async toggleFullscreen(): Promise<void> {
    if (this._isFullscreen()) {
      await this.exitFullscreen();
    } else {
      await this.requestFullscreen();
    }
  }

  /**
   * 检查是否支持全屏API
   */
  isFullscreenSupported(): boolean {
    return !!(
      document.documentElement.requestFullscreen ||
      (document.documentElement as any).webkitRequestFullscreen ||
      (document.documentElement as any).mozRequestFullScreen ||
      (document.documentElement as any).msRequestFullscreen
    );
  }
}